import { useId } from 'react';
import { Button, Input, Select } from '../../../design-system';
import PersonPicker from './PersonPicker';

// Governance vocab — mirrors the enums in models/Client.js. Kept here (not in a
// shared lib) because this is the only surface that edits governance.
export const BOARD_ROLES = [
  { value: 'chairperson', label: 'Chairperson' },
  { value: 'vice_chairperson', label: 'Vice/Deputy Chairperson' },
  { value: 'managing_director', label: 'Managing Director' },
  { value: 'executive_director', label: 'Executive Director' },
  { value: 'whole_time_director', label: 'Whole-Time Director' },
  { value: 'non_executive_director', label: 'Non-Executive Director' },
  { value: 'independent_director', label: 'Independent Director' },
  { value: 'nominee_director', label: 'Nominee Director' },
  { value: 'alternate_director', label: 'Alternate Director' },
  { value: 'additional_director', label: 'Additional Director' },
  // A director holding the seat AS a promoter. For a promoter who ALSO holds
  // another seat — very often the MD — use the "Promoter" checkbox instead:
  // promoter status is orthogonal to the chair, the same way executive is.
  { value: 'promoter_director', label: 'Promoter Director' },
  { value: 'company_secretary', label: 'Company Secretary' },
];

export const COMMITTEE_TYPES = [
  { value: 'audit', label: 'Audit Committee' },
  { value: 'nomination_remuneration', label: 'Nomination & Remuneration' },
  { value: 'risk_management', label: 'Risk Management' },
  { value: 'stakeholders_relationship', label: 'Stakeholders Relationship' },
  { value: 'csr', label: 'CSR' },
  { value: 'esg', label: 'ESG / Sustainability' },
  { value: 'executive', label: 'Executive' },
  { value: 'other', label: 'Other' },
];

export const COMMITTEE_MEMBER_ROLES = [
  { value: 'chair', label: 'Chair' },
  { value: 'member', label: 'Member' },
  { value: 'invitee', label: 'Invitee' },
];

export const OFFICER_TYPES = [
  { value: 'kmp_md_ceo', label: 'KMP — MD / CEO' },
  { value: 'kmp_cfo', label: 'KMP — CFO' },
  { value: 'kmp_company_secretary', label: 'KMP — Company Secretary' },
  { value: 'authorised_signatory', label: 'Authorised Signatory' },
  { value: 'resident_director', label: 'Resident Director' },
  { value: 'compliance_officer', label: 'Compliance Officer' },
  { value: 'data_protection_officer', label: 'Data Protection Officer' },
  { value: 'nodal_officer', label: 'Nodal Officer' },
  { value: 'posh_presiding_officer', label: 'POSH Presiding Officer' },
  { value: 'other', label: 'Other' },
];

export const emptyBoardMember = {
  name: '', role: 'independent_director', isExecutive: false, isPromoter: false,
  // ⚠️ Records only that a matching holding EXISTS in the shareholder list —
  // never the holding itself. A director is NOT a shareholder by default: an
  // independent director is required not to hold a material stake, and plenty
  // of directors hold nothing at all.
  isShareholder: false,
  din: '', pan: '', aadhaar: '', dateOfBirth: '',
  nominatedBy: '', appointedOn: '', resignedOn: '', termEndOn: '',
};
export const emptyCommitteeMember = { name: '', role: 'member', from: '', to: '' };
export const emptyCommittee = { name: '', type: 'audit', charter: '', formedOn: '', members: [] };
export const emptyOfficer = { name: '', officerType: 'kmp_md_ceo', din: '', pan: '', appointedOn: '', ceasedOn: '' };

// ISO/Date -> "YYYY-MM-DD" for <input type="date">; '' for empty.
const toDateInput = (v) => (v ? new Date(v).toISOString().slice(0, 10) : '');

// Server doc -> form-friendly governance state (dates as YYYY-MM-DD strings).
export const hydrateGovernance = (c) => ({
  board: (c.board || []).map((m) => ({
    ...emptyBoardMember, ...m,
    dateOfBirth: toDateInput(m.dateOfBirth),
    appointedOn: toDateInput(m.appointedOn), resignedOn: toDateInput(m.resignedOn), termEndOn: toDateInput(m.termEndOn),
  })),
  committees: (c.committees || []).map((cm) => ({
    ...emptyCommittee, ...cm,
    formedOn: toDateInput(cm.formedOn),
    members: (cm.members || []).map((mem) => ({
      ...emptyCommitteeMember, ...mem, from: toDateInput(mem.from), to: toDateInput(mem.to),
    })),
  })),
  statutoryOfficers: (c.statutoryOfficers || []).map((o) => ({
    ...emptyOfficer, ...o, appointedOn: toDateInput(o.appointedOn), ceasedOn: toDateInput(o.ceasedOn),
  })),
});

// Form state -> payload: drop blank rows and empty optionals so we never send
// "" where a Date is expected. Empty date strings just aren't sent.
export const cleanGovernance = ({ board, committees, statutoryOfficers }) => {
  const stripDates = (row, dateFields) => {
    const out = { ...row };
    dateFields.forEach((f) => {
      if (!out[f]) delete out[f];
    });
    if (out.pan) out.pan = out.pan.toUpperCase();
    return out;
  };
  return {
    board: board
      .filter((m) => m.name.trim())
      .map((m) => stripDates(m, ['dateOfBirth', 'appointedOn', 'resignedOn', 'termEndOn'])),
    committees: committees
      .filter((c) => c.name.trim())
      .map((c) => ({
        ...stripDates(c, ['formedOn']),
        members: (c.members || []).filter((mem) => mem.name.trim()).map((mem) => stripDates(mem, ['from', 'to'])),
      })),
    statutoryOfficers: statutoryOfficers
      .filter((o) => o.name.trim())
      .map((o) => stripDates(o, ['appointedOn', 'ceasedOn'])),
  };
};

// Controlled component: parent owns the three arrays; we hand back the whole
// next array on every edit (same contract as GstinFields).
// `onAddShareholder(member)` is the bridge to the SEPARATE shareholder list:
// ticking "also holds shares" on a director starts a holding row prefilled from
// them, rather than duplicating shareholding fields onto the board. The two
// lists stay distinct — which is the point, since a director may hold nothing.
// `people` is everyone already named anywhere on this client (see
// lib/clientPeople) — the board, the officer list and every committee seat are
// mostly the SAME handful of humans, and retyping a PAN is how one person ends
// up as two.
export default function GovernanceFields({
  board, committees, statutoryOfficers, onBoardChange, onCommitteesChange, onOfficersChange,
  onAddShareholder = null, people = [],
}) {
  const peopleListId = useId();

  // --- board ---
  const setBoardAt = (idx, field, value) =>
    onBoardChange(board.map((m, i) => (i === idx ? { ...m, [field]: value } : m)));
  // Fills BLANKS only — picking somebody must never wipe a value already typed
  // into the row.
  const fillBoardAt = (idx, patch) =>
    onBoardChange(board.map((m, i) => {
      if (i !== idx) return m;
      const next = { ...m };
      for (const [k, v] of Object.entries(patch)) if (!next[k]) next[k] = v;
      return next;
    }));
  const addBoard = () => onBoardChange([...board, { ...emptyBoardMember }]);
  const removeBoard = (idx) => onBoardChange(board.filter((_, i) => i !== idx));

  // --- committees (+ nested members) ---
  const setCommitteeAt = (idx, field, value) =>
    onCommitteesChange(committees.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  const addCommittee = () => onCommitteesChange([...committees, { ...emptyCommittee, members: [] }]);
  const removeCommittee = (idx) => onCommitteesChange(committees.filter((_, i) => i !== idx));
  const setMemberAt = (ci, mi, field, value) =>
    onCommitteesChange(committees.map((c, i) => (
      i === ci ? { ...c, members: c.members.map((m, j) => (j === mi ? { ...m, [field]: value } : m)) } : c
    )));
  const addMember = (ci) =>
    onCommitteesChange(committees.map((c, i) => (i === ci ? { ...c, members: [...c.members, { ...emptyCommitteeMember }] } : c)));
  const removeMember = (ci, mi) =>
    onCommitteesChange(committees.map((c, i) => (i === ci ? { ...c, members: c.members.filter((_, j) => j !== mi) } : c)));

  // --- officers ---
  const setOfficerAt = (idx, field, value) =>
    onOfficersChange(statutoryOfficers.map((o, i) => (i === idx ? { ...o, [field]: value } : o)));
  const addOfficer = () => onOfficersChange([...statutoryOfficers, { ...emptyOfficer }]);
  const removeOfficer = (idx) => onOfficersChange(statutoryOfficers.filter((_, i) => i !== idx));
  const fillOfficerAt = (idx, patch) =>
    onOfficersChange(statutoryOfficers.map((o, i) => {
      if (i !== idx) return o;
      const next = { ...o };
      for (const [k, v] of Object.entries(patch)) if (!next[k]) next[k] = v;
      return next;
    }));
  const fillCommitteeMemberAt = (ci, mi, patch) =>
    onCommitteesChange(committees.map((c, i) => (i !== ci ? c : {
      ...c,
      members: c.members.map((mem, j) => {
        if (j !== mi) return mem;
        const next = { ...mem };
        for (const [k, v] of Object.entries(patch)) if (!next[k]) next[k] = v;
        return next;
      }),
    })));

  return (
    <>
      {/* One datalist for every name input in THIS component. The id comes from
          useId rather than a shared constant: a datalist is referenced by DOM
          id, and two components hard-coding the same one would collide the
          moment both render — an invalid document that fails silently, with one
          list quietly winning. */}
      <datalist id={peopleListId}>
        {people.map((p) => <option key={p.key} value={p.name} />)}
      </datalist>

      {/* Board of Directors */}
      <div className="gstins-subsection">
        <div className="apob-header">
          <span className="ds-field-label">Board of Directors</span>
          <Button type="button" variant="secondary" size="sm" onClick={addBoard}>+ Add Member</Button>
        </div>
        {board.length === 0 && (
          <p className="hint-text">No board members recorded. Add directors, the chairperson, or the company secretary — a resignation date retires a member without losing the history.</p>
        )}
        {board.map((m, idx) => (
          <div key={idx} className="gstin-edit-block">
            <div className="gstin-edit-head">
              <span className="ds-field-label">Member {idx + 1}</span>
              <button type="button" onClick={() => removeBoard(idx)} className="btn-remove-icon">×</button>
            </div>
            <div className="form-row">
              <PersonPicker people={people} onPick={(patch) => fillBoardAt(idx, patch)} fields={['name', 'pan', 'din', 'aadhaar']} />
            </div>
            <div className="form-row">
              <Input label="Name" value={m.name} onChange={(e) => setBoardAt(idx, 'name', e.target.value)} />
              <Select label="Role" value={m.role} onChange={(e) => setBoardAt(idx, 'role', e.target.value)}>
                {BOARD_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </Select>
              <Input label="DIN" value={m.din} onChange={(e) => setBoardAt(idx, 'din', e.target.value)} maxLength={8} />
              <Input label="PAN" value={m.pan} onChange={(e) => setBoardAt(idx, 'pan', e.target.value.toUpperCase())} maxLength={10} />
            </div>
            {/* DIR-3 KYC asks for the Aadhaar and the date of birth on the DIN
                record every single year, and a mismatch is what fails the
                filing. Kept beside the DIN rather than in a document, so the
                pair can be read off the record when the form is being made. */}
            <div className="form-row">
              <Input
                label="Aadhaar"
                value={m.aadhaar}
                onChange={(e) => setBoardAt(idx, 'aadhaar', e.target.value.replace(/\D/g, ''))}
                maxLength={12}
                inputMode="numeric"
                placeholder="12 digits"
              />
              <Input label="Date of Birth" type="date" value={m.dateOfBirth} onChange={(e) => setBoardAt(idx, 'dateOfBirth', e.target.value)} />
            </div>
            <div className="form-row">
              <Input label="Nominated By" value={m.nominatedBy} onChange={(e) => setBoardAt(idx, 'nominatedBy', e.target.value)} placeholder="Investor / lender, if a nominee" />
              <Input label="Appointed On" type="date" value={m.appointedOn} onChange={(e) => setBoardAt(idx, 'appointedOn', e.target.value)} />
              <Input label="Term End" type="date" value={m.termEndOn} onChange={(e) => setBoardAt(idx, 'termEndOn', e.target.value)} />
              <Input label="Resigned On" type="date" value={m.resignedOn} onChange={(e) => setBoardAt(idx, 'resignedOn', e.target.value)} />
            </div>
            <div className="board-flags">
              <label className="checkbox-label">
                <input type="checkbox" checked={m.isExecutive} onChange={(e) => setBoardAt(idx, 'isExecutive', e.target.checked)} />
                Executive director
              </label>
              {/* Orthogonal to the role, like executive status: a promoter is
                  routinely the MD, and MGT-7 / DIR-12 ask who the promoters are
                  whichever chair they sit in. */}
              <label className="checkbox-label">
                <input type="checkbox" checked={!!m.isPromoter} onChange={(e) => setBoardAt(idx, 'isPromoter', e.target.checked)} />
                Promoter
              </label>
              {/* ⚠️ A DIRECTOR IS NOT A SHAREHOLDER. Ticking this does not
                  create a holding — it opens the shareholder list, where the
                  shares actually live and where the cap-table maths can see
                  them. Leaving it unticked is the normal case for an
                  independent director. */}
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!m.isShareholder}
                  onChange={(e) => {
                    setBoardAt(idx, 'isShareholder', e.target.checked);
                    if (e.target.checked) onAddShareholder?.(m);
                  }}
                />
                Also holds shares
              </label>
            </div>
            {m.isShareholder && (
              <p className="hint-text">
                {onAddShareholder
                  ? 'Their shareholding is recorded in Shareholders above — a row has been started with their name, PAN and DIN.'
                  : 'Record their shareholding in the Shareholders list.'}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Committees */}
      <div className="gstins-subsection">
        <div className="apob-header">
          <span className="ds-field-label">Board Committees</span>
          <Button type="button" variant="secondary" size="sm" onClick={addCommittee}>+ Add Committee</Button>
        </div>
        {committees.length === 0 && (
          <p className="hint-text">No committees recorded. Add the Audit, NRC, Risk, CSR or ESG committee and who sits on it.</p>
        )}
        {committees.map((c, ci) => (
          <div key={ci} className="gstin-edit-block">
            <div className="gstin-edit-head">
              <span className="ds-field-label">Committee {ci + 1}</span>
              <button type="button" onClick={() => removeCommittee(ci)} className="btn-remove-icon">×</button>
            </div>
            <div className="form-row">
              <Input label="Name" value={c.name} onChange={(e) => setCommitteeAt(ci, 'name', e.target.value)} />
              <Select label="Type" value={c.type} onChange={(e) => setCommitteeAt(ci, 'type', e.target.value)}>
                {COMMITTEE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
              <Input label="Formed On" type="date" value={c.formedOn} onChange={(e) => setCommitteeAt(ci, 'formedOn', e.target.value)} />
            </div>
            <Input label="Charter" value={c.charter} onChange={(e) => setCommitteeAt(ci, 'charter', e.target.value)} placeholder="Terms of reference (optional)" />

            <div className="committee-members">
              <div className="apob-header">
                <span className="ds-field-label">Members</span>
                <Button type="button" variant="secondary" size="sm" onClick={() => addMember(ci)}>+ Add</Button>
              </div>
              {(c.members || []).length === 0 && <p className="hint-text">No members added.</p>}
              {(c.members || []).map((mem, mi) => (
                <div key={mi} className="repeater-row">
                  {/* A committee seat stores only a name, and this row is
                      already tight — so the people list is offered as a
                      datalist rather than a second control. Type or pick; a
                      name not on the list is still perfectly valid. */}
                  <input
                    className="ds-input"
                    placeholder="Name"
                    list={peopleListId}
                    value={mem.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setMemberAt(ci, mi, 'name', name);
                      // Picking a known person also carries their identity in,
                      // for the day this row grows a PAN column.
                      const hit = people.find((p) => p.name === name);
                      if (hit) fillCommitteeMemberAt(ci, mi, { pan: hit.pan, din: hit.din });
                    }}
                  />
                  <select className="ds-select" value={mem.role} onChange={(e) => setMemberAt(ci, mi, 'role', e.target.value)}>
                    {COMMITTEE_MEMBER_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <input className="ds-input" type="date" value={mem.from} onChange={(e) => setMemberAt(ci, mi, 'from', e.target.value)} title="From" />
                  <input className="ds-input" type="date" value={mem.to} onChange={(e) => setMemberAt(ci, mi, 'to', e.target.value)} title="To" />
                  <button type="button" onClick={() => removeMember(ci, mi)} className="btn-remove-icon">×</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Statutory officers */}
      <div className="gstins-subsection">
        <div className="apob-header">
          <span className="ds-field-label">Statutory Officers</span>
          <Button type="button" variant="secondary" size="sm" onClick={addOfficer}>+ Add Officer</Button>
        </div>
        {statutoryOfficers.length === 0 && (
          <p className="hint-text">No officers recorded. Add KMP (MD/CEO, CFO, CS), the compliance officer, or the POSH presiding officer.</p>
        )}
        {statutoryOfficers.map((o, idx) => (
          <div key={idx} className="gstin-edit-block">
            <div className="gstin-edit-head">
              <span className="ds-field-label">Officer {idx + 1}</span>
              <button type="button" onClick={() => removeOfficer(idx)} className="btn-remove-icon">×</button>
            </div>
            <div className="form-row">
              <PersonPicker people={people} onPick={(patch) => fillOfficerAt(idx, patch)} />
            </div>
            <div className="form-row">
              <Input label="Name" value={o.name} onChange={(e) => setOfficerAt(idx, 'name', e.target.value)} />
              <Select label="Office" value={o.officerType} onChange={(e) => setOfficerAt(idx, 'officerType', e.target.value)}>
                {OFFICER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
              <Input label="DIN" value={o.din} onChange={(e) => setOfficerAt(idx, 'din', e.target.value)} maxLength={8} />
              <Input label="PAN" value={o.pan} onChange={(e) => setOfficerAt(idx, 'pan', e.target.value.toUpperCase())} maxLength={10} />
            </div>
            <div className="form-row">
              <Input label="Appointed On" type="date" value={o.appointedOn} onChange={(e) => setOfficerAt(idx, 'appointedOn', e.target.value)} />
              <Input label="Ceased On" type="date" value={o.ceasedOn} onChange={(e) => setOfficerAt(idx, 'ceasedOn', e.target.value)} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
